/** Procedurally drawn White Rabbit (with waistcoat). Origin is the center of the feet. */
export interface RabbitPose {
  facing: number;
  vx: number;
  vy: number;
  onGround: boolean;
  climbing: boolean;
  runPhase: number;
  squash: number;
  time: number;
  /** 0..1 dive progress (shrinks into the ground). */
  dive: number;
  flash: boolean;
  alpha: number;
}

const OUT = '#26282c';

function ellipse(ctx: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number, rot = 0): void {
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, rot, 0, Math.PI * 2);
}

export function drawRabbit(ctx: CanvasRenderingContext2D, x: number, y: number, p: RabbitPose): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.globalAlpha = p.alpha;
  const running = p.onGround && Math.abs(p.vx) > 20;
  const air = !p.onGround && !p.climbing;
  let sx = 1 + p.squash * 0.55;
  let sy = 1 - p.squash;
  if (!p.onGround && !p.climbing) {
    const stretch = Math.min(0.22, Math.abs(p.vy) / 2600);
    sx -= stretch * 0.6;
    sy += stretch;
  }
  const breathe = !running && p.onGround ? Math.sin(p.time * 3.2) * 0.025 : 0;
  sy += breathe;
  if (p.dive > 0) {
    const d = Math.min(1, p.dive);
    sx *= 1 - d * 0.7;
    sy *= 1 - d;
    ctx.rotate(Math.sin(d * 12) * 0.3 * d);
  }
  ctx.scale(sx * (p.facing < 0 ? -1 : 1), sy);
  const bob = running ? -Math.abs(Math.sin(p.runPhase * 2.2)) * 2.2 : 0;
  ctx.translate(0, bob);
  ctx.lineWidth = 1.3;
  ctx.strokeStyle = OUT;
  ctx.lineJoin = 'round';

  const body = p.flash ? '#ffd0d0' : '#fbfbfd';
  const shade = p.flash ? '#f0a0a0' : '#e4e6ee';

  // Ears: angle depends on motion.
  const earBack = air ? (p.vy < 0 ? 0.75 : 0.15) : running ? 0.5 + Math.sin(p.runPhase * 2.2) * 0.12 : 0.28;
  const flop = Math.sin(p.time * 1.7) > 0.96 ? 0.25 : 0;
  for (const [ox, extra] of [
    [-1.2, 0.18],
    [1.6, 0],
  ] as const) {
    ctx.save();
    ctx.translate(1.5 + ox, -18.5);
    ctx.rotate(-earBack - extra - (ox > 0 ? flop : 0));
    ellipse(ctx, 0, -6.5, 2.6, 7.2);
    ctx.fillStyle = body;
    ctx.fill();
    ctx.stroke();
    ellipse(ctx, 0.2, -6.3, 1.1, 5.2);
    ctx.fillStyle = '#f6a9c0';
    ctx.fill();
    ctx.restore();
  }

  // Tail.
  ellipse(ctx, -7.2, -6.5, 2.8, 2.6);
  ctx.fillStyle = body;
  ctx.fill();
  ctx.stroke();

  // Feet.
  const a = running ? Math.sin(p.runPhase * 2.2) * 3.2 : p.climbing ? Math.sin(p.time * 10) * 2 : 0;
  const tuck = air ? -2 : 0;
  ctx.fillStyle = body;
  ellipse(ctx, -2.8 + a, -1.6 + tuck, 3.4, 1.8);
  ctx.fill();
  ctx.stroke();
  ellipse(ctx, 3 - a, -1.6 + tuck, 3.4, 1.8);
  ctx.fill();
  ctx.stroke();

  // Body.
  ellipse(ctx, 0, -7.8, 6.6, 6.4);
  ctx.fillStyle = body;
  ctx.fill();
  ctx.stroke();
  // Belly shading.
  ellipse(ctx, -1.5, -6, 3.5, 3.4);
  ctx.fillStyle = shade;
  ctx.fill();

  // Red waistcoat with a gold pocket-watch chain.
  ctx.beginPath();
  ctx.moveTo(1.2, -13.4);
  ctx.quadraticCurveTo(7.4, -11.6, 6.4, -5.2);
  ctx.quadraticCurveTo(4, -3.2, 1.6, -4.4);
  ctx.closePath();
  ctx.fillStyle = '#c8323c';
  ctx.fill();
  ctx.stroke();
  ctx.strokeStyle = '#e0b030';
  ctx.lineWidth = 0.9;
  ctx.beginPath();
  ctx.moveTo(2.2, -8.6);
  ctx.quadraticCurveTo(4.2, -7, 5.8, -8.2);
  ctx.stroke();
  ctx.strokeStyle = OUT;
  ctx.lineWidth = 1.3;

  // Head.
  ellipse(ctx, 3.2, -15.6, 5.4, 4.9);
  ctx.fillStyle = body;
  ctx.fill();
  ctx.stroke();
  // Eye (blinks).
  const blink = Math.sin(p.time * 0.9) > 0.985;
  ctx.fillStyle = OUT;
  if (blink) ctx.fillRect(4.6, -16.2, 2.2, 0.8);
  else {
    ellipse(ctx, 5.6, -16.2, 1.1, 1.35);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.fillRect(5.7, -16.9, 0.6, 0.6);
  }
  // Nose + cheek.
  ctx.fillStyle = '#ef8fae';
  ellipse(ctx, 8.4, -14.6, 0.95, 0.8);
  ctx.fill();
  ctx.fillStyle = 'rgba(246,150,170,0.35)';
  ellipse(ctx, 4.3, -13.4, 1.6, 1);
  ctx.fill();
  ctx.restore();
}
